import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useAppLoad } from '../../contexts/AppLoadContext';
import spotlightService from '../../services/SpotlightService';
import ReactDOM from 'react-dom';

// Constants for timeline calculations
const SLOT_WIDTH = 200; // Width in pixels for each 30-minute slot
const SECONDS_PER_SLOT = 1800; // 30 minutes in seconds
const TOTAL_HOURS = 12; // Show 12 hours total
const SLOTS_TO_SHOW = TOTAL_HOURS * 2; // 2 slots per hour

// Calculate program position and width based on time
const calculateProgramLayout = (program, minTime, pixelsPerSecond) => {
  // Calculate position relative to minTime
  const relativeStart = Math.max(program.startTime - minTime, 0);
  const left = relativeStart * pixelsPerSecond;

  // Calculate width based on program duration
  const maxTime = minTime + SECONDS_PER_SLOT * SLOTS_TO_SHOW;
  const programStart = Math.max(program.startTime, minTime);
  const programEnd = Math.min(program.endTime, maxTime); // Use full program duration from API

  const duration = programEnd - programStart;
  const width = Math.max(duration * pixelsPerSecond, 0);

  return { left, width };
};

// Transform EPG API data to grid format with Tubi-style continuous timeline
const transformEPGData = epgResponse => {
  if (!epgResponse?.channels) {
    return { channels: [], programs: [], timeSlots: [] };
  }

  const channels = epgResponse.channels.map(channel => ({
    id: channel._id,
    slug: channel.slug,
    name: channel.name,
    description: channel.description || '',
    channelNumber: channel.channel_number,
    logo: channel.logo,
    poster: channel.poster,
    category: channel.category?.[0] || 'General',
    language: channel.language || 'en',
    videoId: channel.video_id,
  }));

  // Set minTime to 30 minutes before the start of the current 30-minute slot
  const now = Math.floor(Date.now() / 1000);
  const currentSlotStart = Math.floor(now / SECONDS_PER_SLOT) * SECONDS_PER_SLOT;
  const minTime = currentSlotStart - SECONDS_PER_SLOT; // Start 30 minutes earlier

  // Calculate maxTime ensuring it's always ahead of minTime even across midnight
  const rawMaxTime = minTime + SECONDS_PER_SLOT * SLOTS_TO_SHOW;

  // Adjust maxTime to ensure it's on the next day if it crosses midnight
  const minTimeDate = new Date(minTime * 1000);
  const maxTimeDate = new Date(rawMaxTime * 1000);

  // If maxTime is on the same day but earlier than minTime, it means we've crossed midnight
  if (maxTimeDate.getHours() < minTimeDate.getHours()) {
    // Add 24 hours worth of seconds
    maxTimeDate.setDate(maxTimeDate.getDate() + 1);
  }

  const maxTime = Math.floor(maxTimeDate.getTime() / 1000);

  // Extract all programs and create continuous timeline
  const allPrograms = [];

  epgResponse.channels.forEach(channel => {
    if (Array.isArray(channel.program)) {
      channel.program.forEach(program => {
        let startTime = program.starts_at;
        let endTime = program.ends_at;

        // If the program crosses midnight and appears to be from the previous day,
        // adjust its timestamps to the next day
        if (startTime < minTime && endTime > minTime) {
          const startDate = new Date(startTime * 1000);
          startDate.setDate(startDate.getDate() + 1);
          startTime = Math.floor(startDate.getTime() / 1000);

          const endDate = new Date(endTime * 1000);
          endDate.setDate(endDate.getDate() + 1);
          endTime = Math.floor(endDate.getTime() / 1000);
        }

        const duration = endTime - startTime;

        // Only include programs that overlap with our window and have valid duration
        if (endTime > minTime && startTime < maxTime && duration > 0) {
          allPrograms.push({
            id: program.program_id,
            channelId: channel._id,
            title: program.program_title,
            description: program.program_description || 'No description available',
            startTime,
            endTime,
            duration,
            episodeTitle: program.episode_title || '',
            episode: program.episode,
            season: program.season,
            part: program.part,
            language: program.program_language || 'en',
            videoId: program.video_id,
          });
        }
      });
    }
  });

  // Create time slots every 30 minutes for the header
  const timeSlots = [];
  for (let i = 0; i < SLOTS_TO_SHOW; i++) {
    const timestamp = minTime + i * SECONDS_PER_SLOT;
    timeSlots.push({
      id: `slot-${timestamp}`,
      timestamp,
      time: new Date(timestamp * 1000).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
    });
  }

  return { channels, programs: allPrograms, timeSlots, minTime, maxTime };
};

// Get current time indicator position
const getCurrentTimePosition = (minTime, pixelsPerSecond) => {
  const now = Math.floor(Date.now() / 1000);
  const position = (now - minTime) * pixelsPerSecond;
  return Math.max(0, position); // Ensure position is never negative
};

// Check if a program is currently playing
const isCurrentlyPlaying = (startTime, endTime) => {
  const now = Math.floor(Date.now() / 1000);
  return now >= startTime && now < endTime;
};

// Helper to format duration as 'X hr Y min' or 'Z min'
function formatDuration(durationSeconds) {
  const totalMinutes = Math.round(durationSeconds / 60);
  if (totalMinutes >= 60) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return minutes === 0
      ? `${hours} hr${hours > 1 ? 's' : ''}`
      : `${hours} hr${hours > 1 ? 's' : ''} ${minutes} min`;
  }
  return `${totalMinutes} min`;
}

const EPGGrid = ({
  data,
  isLoading,
  error,
  onChannelSelect,
  onProgramSelect,
  selectedChannelId,
  selectedProgramId,
  fetchEPGData,
}) => {
  const parentRef = useRef();
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [currentTime, setCurrentTime] = useState(Math.floor(Date.now() / 1000));
  const [scrollPosition, setScrollPosition] = useState(0);
  const [modalExiting, setModalExiting] = useState(false);

  // Calculate timeline dimensions
  const timelineWidth = SLOT_WIDTH * SLOTS_TO_SHOW;
  const pixelsPerSecond = SLOT_WIDTH / SECONDS_PER_SLOT;

  // Calculate current time position based on current time
  const currentTimePosition = useMemo(() => {
    return getCurrentTimePosition(data.minTime, pixelsPerSecond);
  }, [data.minTime, pixelsPerSecond, currentTime]);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Math.floor(Date.now() / 1000));
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const { channels, programs, timeSlots, minTime, maxTime } = data;

  // Virtualize rows (channels) - Show all channels without scrolling
  const rowVirtualizer = useVirtualizer({
    count: channels.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 70, // Reduced from 90px to 70px for more compact view
    overscan: channels.length, // Show all channels
  });

  // Get programs for a specific channel
  const getProgramsForChannel = channelId => {
    const channelPrograms = programs.filter(p => p.channelId === channelId);
    // Dedupe programs by id and ensure valid duration
    const uniquePrograms = new Map();
    channelPrograms.forEach(p => {
      if (p.duration > 0) {
        uniquePrograms.set(p.id, p);
      }
    });
    return Array.from(uniquePrograms.values()).sort((a, b) => a.startTime - b.startTime);
  };

  // Navigation functions for Tubi-style arrows
  const scrollLeft = () => {
    if (parentRef.current) {
      const newPosition = Math.max(0, scrollPosition - 400);
      parentRef.current.scrollLeft = newPosition;
      setScrollPosition(newPosition);
    }
  };

  const scrollRight = () => {
    if (parentRef.current) {
      const maxScroll = timelineWidth - parentRef.current.clientWidth;
      const newPosition = Math.min(maxScroll, scrollPosition + 400);
      parentRef.current.scrollLeft = newPosition;
      setScrollPosition(newPosition);
    }
  };

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (parentRef.current) {
        setScrollPosition(parentRef.current.scrollLeft);
      }
    };

    const element = parentRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
      return () => element.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Reset modalExiting when opening
  useEffect(() => {
    if (selectedProgram) setModalExiting(false);
  }, [selectedProgram]);

  // Handle modal close with animation
  const handleModalClose = () => {
    setModalExiting(true);
    setTimeout(() => {
      setModalExiting(false);
      setSelectedProgram(null);
    }, 300);
  };

  // Loading state
  if (isLoading) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#1e293b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#a855f7', fontSize: '24px', marginBottom: '16px' }}>
            Loading EPG Data...
          </div>
          <div style={{ color: '#94a3b8', fontSize: '16px' }}>Fetching live TV schedule...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#1e293b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ color: '#ef4444', fontSize: '24px', marginBottom: '16px' }}>
            Failed to Load EPG
          </div>
          <div style={{ color: '#94a3b8', marginBottom: '24px' }}>{error}</div>
          <button
            onClick={fetchEPGData}
            style={{
              padding: '12px 24px',
              backgroundColor: '#7c3aed',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (channels.length === 0) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#1e293b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: 'white', fontSize: '24px', marginBottom: '12px' }}>
            No EPG Data Available
          </div>
          <div style={{ color: '#94a3b8', fontSize: '16px' }}>No channels or programs found.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gradient-to-br from-gray-900 to-gray-800">
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-gray-800 to-gray-700 border-b border-gray-600/50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2 flex items-center space-x-3">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <span>ChillFree TV</span>
            </h2>
            <p className="text-gray-300 text-sm">
              {channels.length} channels • {timeSlots.length} time slots • {programs.length}{' '}
              programs
            </p>
          </div>
          <div className="flex items-center">
            <span className="text-gray-400 text-sm">
              Last updated: {new Date().toLocaleTimeString()}
            </span>
          </div>
        </div>
      </div>

      {/* Main grid container with fixed channel list and scrollable timeline */}
      <div className="relative flex flex-col h-full bg-gray-900">
        {/* Scrollable container */}
        <div className="flex overflow-x-auto">
          {/* Fixed channel list */}
          <div
            className="sticky left-0 z-10 bg-gray-900 flex-shrink-0 border-r border-gray-600/50"
            style={{ width: '120px' }}
          >
            {/* Channel list header */}
            <div className="h-[70px] bg-gradient-to-b from-gray-700 to-gray-800 border-b border-gray-600/50">
              <div className="h-full flex items-center justify-center">
                <span className="text-white text-sm font-semibold">Channels</span>
              </div>
            </div>
            {/* Channel list */}
            <div>
              {channels.map(channel => (
                <div
                  key={channel.id}
                  className={`
                    h-[100px] p-2 flex items-center justify-center cursor-pointer
                    border-b border-gray-600/50 transition-colors
                    ${channel.id === selectedChannelId ? 'bg-blue-900/30' : 'hover:bg-gray-800/50'}
                  `}
                  onClick={() => onChannelSelect && onChannelSelect(channel)}
                  title={`Switch to ${channel.name}`}
                >
                  <div className="w-full h-full flex items-center justify-center">
                    <img
                      src={channel.logo}
                      alt={channel.name}
                      className="max-w-[80%] max-h-[80%] object-contain"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Scrollable timeline and programs */}
          <div className="flex-1 relative">
            {/* Time slots header - continuous timeline */}
            <div
              style={{
                width: `${timelineWidth}px`,
                height: '70px',
                position: 'relative',
              }}
              className="bg-gradient-to-b from-gray-700 to-gray-800"
            >
              {timeSlots.map((timeSlot, index) => {
                const position = index * SLOT_WIDTH;
                return (
                  <div
                    key={timeSlot.id}
                    style={{
                      position: 'absolute',
                      left: `${position}px`,
                      top: 0,
                      height: '70px',
                      width: `${SLOT_WIDTH}px`,
                    }}
                    className="border-r border-gray-600/50 p-2 flex flex-col justify-center hover:bg-gray-600/30 transition-colors"
                  >
                    <span className="text-white text-sm font-semibold">{timeSlot.time}</span>
                  </div>
                );
              })}

              {/* Current time indicator in timeline header */}
              {currentTime >= minTime && currentTime <= maxTime && (
                <div
                  style={{
                    position: 'absolute',
                    left: `${currentTimePosition}px`,
                    top: 0,
                    height: '70px',
                    width: '2px',
                    transform: 'translateX(-1px)',
                  }}
                  className="bg-red-500 z-30"
                >
                  <div className="absolute -translate-x-[9px] -top-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <div className="absolute -translate-x-[34px] top-6 bg-red-500 text-white text-xs px-2 py-1 rounded">
                    LIVE
                  </div>
                </div>
              )}
            </div>

            {/* Programs grid with current time indicator */}
            <div style={{ width: `${timelineWidth}px`, position: 'relative' }}>
              {/* Current time vertical line indicator */}
              {currentTime >= minTime && currentTime <= maxTime && (
                <div
                  style={{
                    position: 'absolute',
                    left: `${currentTimePosition}px`,
                    top: 0,
                    height: '100%',
                    width: '2px',
                    transform: 'translateX(-1px)',
                    zIndex: 10,
                  }}
                  className="bg-red-500 pointer-events-none"
                />
              )}

              {channels.map(channel => (
                <div key={channel.id} className="relative h-[100px] border-b border-gray-600/50">
                  {/* Render programs for this channel */}
                  {getProgramsForChannel(channel.id).map(program => {
                    const layout = calculateProgramLayout(program, minTime, pixelsPerSecond);
                    const isSelectedProgram = program.id === selectedProgramId;
                    const isCurrentProgram = isCurrentlyPlaying(program.startTime, program.endTime);

                    return (
                      <div
                        key={program.id}
                        style={{
                          position: 'absolute',
                          left: `${layout.left}px`,
                          top: '8px',
                          width: `${layout.width}px`,
                          height: 'calc(100% - 16px)',
                        }}
                        className={
                          `rounded-lg border cursor-pointer transition-all duration-200 p-epg relative ` +
                          (isSelectedProgram
                            ? 'ring-2 ring-[#56bdaf] bg-[#56bdaf]/20 border-[#56bdaf]'
                            : isCurrentProgram
                            ? 'bg-gradient-to-r from-[#56bdaf]/40 to-[#56bdaf]/10 border-[#56bdaf]/60 hover:from-[#56bdaf]/60 hover:to-[#56bdaf]/20'
                            : 'bg-gradient-to-r from-blue-900/40 to-blue-800/30 border-blue-500/30 hover:from-blue-800/50 hover:to-blue-700/40')
                        }
                        onClick={() => setSelectedProgram(program)}
                      >
                        <div className="h-full flex flex-col justify-between overflow-hidden">
                          <div>
                            <div className="font-medium text-sm text-white truncate">
                              {program.title}
                            </div>
                            <div className="text-xs text-gray-400">
                              {new Date(program.startTime * 1000).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                              })}
                            </div>
                          </div>
                          {layout.width > 100 && (
                            <div className="text-xs text-gray-300 truncate">
                              {program.description}
                            </div>
                          )}
                        </div>

                        {/* Current time progress overlay for live programs */}
                        {isCurrentProgram && (
                          <div
                            style={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              width: `${Math.min(
                                ((currentTime - program.startTime) /
                                  (program.endTime - program.startTime)) *
                                  100,
                                100,
                              )}%`,
                              height: '100%',
                            }}
                            className="bg-gradient-to-r from-[#56bdaf]/30 to-transparent rounded-lg pointer-events-none"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Program Detail Modal */}
      {selectedProgram && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 program-modal-portal"
          style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}
          onClick={handleModalClose}
        >
          <div
            className={`bg-primary border border-[#56bdaf] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-auto program-modal ${
              modalExiting ? 'modal-exit' : 'modal-enter'
            }`}
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-white">{selectedProgram.title}</h3>
                <button
                  onClick={handleModalClose}
                  className="text-[#56bdaf] hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {selectedProgram.episodeTitle && (
                <h4 className="text-lg text-[#56bdaf] mb-3">{selectedProgram.episodeTitle}</h4>
              )}

              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div>
                  <span className="text-[#56bdaf]">Start Time:</span>
                  <p className="text-white">
                    {new Date(selectedProgram.startTime * 1000).toLocaleString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                      month: 'short',
                      day: 'numeric',
                    })}
                  </p>
                </div>
                <div>
                  <span className="text-[#56bdaf]">Duration:</span>
                  <p className="text-white">{formatDuration(selectedProgram.duration)}</p>
                </div>
                {selectedProgram.season && (
                  <div>
                    <span className="text-[#56bdaf]">Season:</span>
                    <p className="text-white">{selectedProgram.season}</p>
                  </div>
                )}
                {selectedProgram.episode && (
                  <div>
                    <span className="text-[#56bdaf]">Episode:</span>
                    <p className="text-white">{selectedProgram.episode}</p>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <span className="text-[#56bdaf]">Description:</span>
                <p className="text-white mt-1">{selectedProgram.description}</p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleModalClose}
                  className="px-4 py-2 bg-[#56bdaf] text-white rounded hover:bg-[#4aa89c] transition-colors"
                >
                  Close
                </button>
                {/* Show Watch Now only if program is live */}
                {currentTime >= selectedProgram.startTime &&
                  currentTime < selectedProgram.endTime && (
                    <button
                      className="px-4 py-2 bg-[#56bdaf] text-white rounded hover:bg-[#4aa89c] transition-colors"
                      onClick={() => {
                        if (onProgramSelect) onProgramSelect(selectedProgram);
                        setSelectedProgram(null);
                      }}
                    >
                      Watch Now
                    </button>
                  )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Enhanced Footer with Statistics */}
      <div className="p-4 bg-gradient-to-r from-[#2a2a2a] to-[#1a1a1a] border-t border-[#56bdaf]/30">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-[#56bdaf] rounded-full"></div>
              <span className="text-[#56bdaf] font-medium">{channels.length}</span>
              <span className="text-gray-400">channels</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-[#56bdaf] rounded-full"></div>
              <span className="text-[#56bdaf] font-medium">{programs.length}</span>
              <span className="text-gray-400">programs</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-[#56bdaf] rounded-full animate-pulse"></div>
              <span className="text-[#56bdaf] font-medium">LIVE</span>
              <span className="text-gray-400">streaming</span>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-400">
              Last updated:{' '}
              {new Date().toLocaleString('en-US', {
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EPGGrid;
